import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  handleBasicsChange,
  handleBasicsSwitchChange,
  handleDateFormatChange,
  handleTextFormatChange,
} from "../../../Redux/reducers/questionSlice";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { ChangeEvent, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { builderGetUploadUrl } from "../../../utils/upload";
import { useParams } from "react-router-dom";
import Add from "@mui/icons-material/Add";
import { Visibility } from "@mui/icons-material";
import MDEditor, { commands } from "@uiw/react-md-editor/nohighlight";
import rehypeSanitize from "rehype-sanitize";
import MarkdownPreview from "./MarkdownPreview";
import RepeatedMeasure from "./RepeatedMeasure";
import { DeleteIcon } from "../Icons";

type Props = {
  isEdit: boolean;
  question: any;
};

export const requiredStyles: SxProps = {
  "&::after": {
    content: '"*"',
    color: "red",
    marginLeft: "3px",
  },
};

export const uploadWrapper: SxProps = {
  width: 200,
  height: 140,
  borderRadius: "8px",
  border: 2,
  borderColor: "primary.main",
  borderStyle: "dashed",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  py: 2,
  px: 4,
  "&:hover": {
    bgcolor: "secondary.dark",
    cursor: "pointer",
  },
};

export const previewWrapper: SxProps = {
  width: 200,
  height: 140,
  borderRadius: "8px",
  border: 2,
  borderColor: "primary.main",
  borderStyle: "dashed",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  py: 2,
  px: 4,
  "&:hover": {
    bgcolor: "secondary.dark",
    cursor: "pointer",
  },
};

export const BasicTab = ({ isEdit, question }: Props) => {
  const { id: studyId } = useParams();
  const dispatch = useAppDispatch();

  const questionList = useAppSelector(({ question }) => question.questions);
  const questionValidations = useAppSelector(
    ({ question }) => question.questionValidations
  );
  const isBasicError = useAppSelector(({ question }) => question.isBasicError);
  const isPreview = useAppSelector(({ question }) => !question.editable);
  const formId = useAppSelector(({ question }) => question.formId);
  const formLevel = useAppSelector(({ question }) => question.formLevel);
  const formCategory = useAppSelector(({ question }) => question.formCategory);

  const varnameScopeHelper =
    formCategory === "survey"
      ? "You can use {varname} for dynamic values from this form or a screener form in this study."
      : formCategory === "screening"
        ? "You can use {varname} for dynamic values from this form only."
        : null;

  const previewUrl = question?.signedImageUrl;

  const [loading, setLoading] = useState(false);
  const [allowRemark, setAllowRemark] = useState(
    !!question?.properties?.remarkText
  );

  const [displayMarkdown, setMarkdown] = useState(false);

  const onChange = (
    key: string,
    value: string | boolean,
    isProp?: boolean,
    convertToNum?: boolean
  ) => {
    if (!isPreview)
      dispatch(
        handleBasicsChange({
          key,
          value,
          isProp,
          convertToNum,
        })
      );
  };

  const showMarkdown = () => {
    setMarkdown(true);
  };

  const closeMarkdown = () => {
    setMarkdown(false);
  };

  const onSwitch = (key: string, event: any) => {
    dispatch(handleBasicsSwitchChange({ key, value: event.target.checked }));
  };

  const handleAllowRemark = (e: ChangeEvent<HTMLInputElement>) => {
    setAllowRemark(e.target.checked);
    onChange("remarkText", e.target.checked ? "Enter Remark" : "", true);
  };

  const handleDisplayNumber = (e: ChangeEvent<HTMLInputElement>) => {
    let isValid = true;
    if (e.target.value) {
      const regex = /^[a-zA-Z0-9.#]*$/;
      isValid = !!e.target.value.match(regex);
    }
    if (isValid) {
      onChange("displayNumber", e.target.value, true);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        const file = acceptedFiles?.[0];
        if (file) {
          if (file.size > 5 * 1024 * 1024) {
            toastMessage("warning", "File Size cannot be greater than 5 MB!");
            return;
          }

          setLoading(true);

          const obj = await builderGetUploadUrl(
            file,
            studyId,
            formId,
            formLevel
          );

          if (obj.postUploadImageUrl) {
            dispatch(
              handleBasicsChange({
                key: "imageUrl",
                value: obj.postUploadImageUrl,
              })
            );
          }
          if (obj.previewUrl) {
            dispatch(
              handleBasicsChange({
                key: "signedImageUrl",
                value: obj.previewUrl,
              })
            );
          }

          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [dispatch, formId, studyId, formLevel]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "image/jpg": [],
      "image/jpeg": [],
      "image/png": [],
      // "application/pdf": [],
    },
    disabled: isPreview,
  });

  const removeImage = () => {
    dispatch(handleBasicsChange({ key: "imageUrl", value: null }));
    dispatch(handleBasicsChange({ key: "signedImageUrl", value: null }));
  };

  return (
    <Stack gap={2} py={1}>
      {isBasicError && (
        <Typography variant="caption" fontWeight="medium" color={"red"}>
          {isBasicError}
        </Typography>
      )}

      <Stack gap={1}>
        {loading ? (
          <Box sx={{ color: "text.secondary" }}>
            <CircularProgress />
          </Box>
        ) : previewUrl ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={"Upload Image"} placement="right">
              <Box
                {...getRootProps({ className: "dropzone" })}
                sx={previewWrapper}
              >
                <input {...getInputProps()} />
                <Avatar
                  src={previewUrl}
                  variant="rounded"
                  sx={{ width: 200, height: 140 }}
                />
              </Box>
            </Tooltip>
            {!isPreview && (
              <IconButton
                color={"error"}
                sx={{ height: 40, width: 40 }}
                onClick={removeImage}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ) : (
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={1}
            {...getRootProps({ className: "dropzone" })}
            sx={{ ...uploadWrapper }}
          >
            <input {...getInputProps()} />

            <Stack alignItems={"center"} gap={1}>
              <Add fontSize="medium" sx={{ color: "text.secondary" }} />
              <Typography
                sx={{ color: "text.secondary" }}
                variant="body1"
                fontWeight={"medium"}
                textAlign={"center"}
              >
                Add Image <br />
                <Typography
                  sx={{ color: "text.secondary" }}
                  component={"span"}
                  variant="body1"
                >
                  or drag and drop
                </Typography>
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>

      <Stack>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography
            variant="body1"
            fontWeight={"regular"}
            sx={requiredStyles}
          >
            Label
          </Typography>
          <IconButton onClick={showMarkdown} size="small">
            <Visibility fontSize="small" />
          </IconButton>
        </Stack>

        <div className="container">
          <MDEditor
            data-color-mode="light"
            value={question.label}
            onChange={(value) => {
              onChange("label", value || "");
            }}
            previewOptions={{
              rehypePlugins: [[rehypeSanitize]],
            }}
            textareaProps={{
              placeholder: "Enter text here",
              readOnly: isPreview,
            }}
            extraCommands={[]}
            commands={[
              commands.bold,
              commands.italic,
              commands.strikethrough,
              commands.quote,
              commands.unorderedListCommand,
              commands.orderedListCommand,
            ]}
            visibleDragbar={false}
            preview="edit"
          />
        </div>
        {varnameScopeHelper && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            {varnameScopeHelper}
          </Typography>
        )}
      </Stack>

      {!isEdit && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Position
          </Typography>
          <FormControl fullWidth>
            <Select
              fullWidth
              id="position"
              value={question.position || ""}
              onChange={(e) => onChange("position", e.target.value)}
            >
              <MenuItem value={1}>Place at the Top</MenuItem>
              {questionList.map((ques) => (
                <MenuItem key={ques.id} value={ques.position + 1}>
                  Place after Question {ques.position}. ({ques.varname}):{" "}
                  {ques.label.length > 50
                    ? ques.label.slice(0, 50) + "..."
                    : ques.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}

      <Stack gap={1}>
        <Typography variant="body1" fontWeight={"regular"} sx={requiredStyles}>
          Variable Name
        </Typography>
        <TextField
          name="varname"
          fullWidth
          placeholder="Variable Name"
          value={question.varname}
          onChange={(e) => onChange("varname", e.target.value)}
          InputProps={{
            readOnly: isPreview,
          }}
          error={questionValidations.varname ? true : false}
          helperText={
            questionValidations.varname ? questionValidations.varname : null
          }
        />
      </Stack>

      {["statement", "summary"].includes(question.type) && (
        <Stack gap={1}>
          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={question.properties.showIndex || false}
                onChange={(e) => {
                  onSwitch("showIndex", e);
                  if (!e.target.checked) {
                    onChange("displayNumber", "", true);
                  }
                }}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Show Display Number
              </Typography>
            }
          />
        </Stack>
      )}

      {!(
        ["statement", "summary"].includes(question.type) &&
        !question?.properties?.showIndex
      ) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Display Number
          </Typography>
          <TextField
            name="displayNumber"
            inputProps={{
              maxLength: 6,
            }}
            fullWidth
            placeholder="Display Number"
            value={question.properties.displayNumber || ""}
            onChange={handleDisplayNumber}
            InputProps={{
              readOnly: isPreview,
            }}
          />
        </Stack>
      )}

      {![
        "statement",
        "summary",
        "randomize",
        "eligibility",
        "repeated_measure",
      ].includes(question.type) && (
        <Stack gap={1}>
          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={question.properties.required}
                onChange={(e) => onSwitch("required", e)}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Required
              </Typography>
            }
          />
        </Stack>
      )}

      {["repeated_measure"].includes(question.type) && (
        <Stack gap={1}>
          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={question.properties.showAll}
                onChange={(e) => onChange("showAll", e.target.checked, true)}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Show Repeated Measures from all Visits
              </Typography>
            }
          />
        </Stack>
      )}

      {["repeated_measure"].includes(question.type) &&
        formLevel === "study" && <RepeatedMeasure question={question} />}

      {![
        "statement",
        "summary",
        "randomize",
        "eligibility",
        "repeated_measure",
      ].includes(question.type) && (
        <Stack gap={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body1" fontWeight={"regular"}>
              PII Classification
            </Typography>
            {question.piiType && question.piiType !== "none" && (
              <Chip
                label={question.piiType === "direct" ? "Direct Identifier" : "Indirect Identifier"}
                size="small"
                color={question.piiType === "direct" ? "error" : "warning"}
                variant="outlined"
              />
            )}
          </Stack>
          <FormControl fullWidth>
            <Select
              readOnly={isPreview}
              fullWidth
              id="piiType"
              value={question.piiType || "none"}
              onChange={(e) => onChange("piiType", e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="direct">Direct Identifier (name, email, phone, ID)</MenuItem>
              <MenuItem value="indirect">Indirect Identifier (date, ZIP/postcode)</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      {["date"].includes(question.type) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Format
          </Typography>
          <FormControl fullWidth>
            <Select
              readOnly={isPreview}
              fullWidth
              id="format"
              value={question.properties.format}
              onChange={(e) =>
                dispatch(handleDateFormatChange({ format: e.target.value }))
              }
            >
              <MenuItem value={"DD/MM/YYYY"}>DD/MM/YYYY</MenuItem>
              <MenuItem value={"MM/DD/YYYY"}>MM/DD/YYYY</MenuItem>
              <MenuItem value={"DD/MM/YYYY HH:mm"}>DD/MM/YYYY HH:mm</MenuItem>
              <MenuItem value={"MM/DD/YYYY HH:mm"}>MM/DD/YYYY HH:mm</MenuItem>
              <MenuItem value={"HH:mm"}>HH:mm</MenuItem>
              <MenuItem value={"HH:mm:ss"}>HH:mm:ss</MenuItem>
              <MenuItem value={"YYYY"}>YYYY</MenuItem>
              <MenuItem value={"MM/YYYY"}>MM/YYYY</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      {["text"].includes(question.type) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Format
          </Typography>
          <FormControl fullWidth>
            <Select
              readOnly={isPreview}
              fullWidth
              id="format"
              displayEmpty
              value={question.properties.format || ""}
              onChange={(e) =>
                dispatch(handleTextFormatChange({ format: e.target.value }))
              }
            >
              <MenuItem value={""}>No Format</MenuItem>
              <MenuItem value={"email"}>Email</MenuItem>
              <MenuItem value={"alphabets"}>Alphabets Only</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      {["text"].includes(question.type) && (
        <Stack gap={1}>
          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={question.properties.allowMultiline}
                onChange={(e) => onSwitch("allowMultiline", e)}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Allow Multiline
              </Typography>
            }
          />
        </Stack>
      )}

      {["number"].includes(question.type) && (
        <>
          <Stack gap={1}>
            <FormControlLabel
              disabled={isPreview}
              control={
                <Switch
                  checked={question.properties.allowDecimal}
                  onChange={(e) => onSwitch("allowDecimal", e)}
                />
              }
              label={
                <Typography variant="body1" fontWeight={"regular"}>
                  Allow Decimal
                </Typography>
              }
            />
          </Stack>

          <Stack gap={1}>
            <Typography variant="body1" fontWeight={"regular"}>
              Measurement Unit
            </Typography>
            <TextField
              name="measurementUnit"
              fullWidth
              placeholder="Measurement Unit"
              value={question.properties.measurementUnit}
              onChange={(e) =>
                onChange("measurementUnit", e.target.value, true)
              }
              InputProps={{
                readOnly: isPreview,
              }}
            />
          </Stack>

          <Stack gap={1}>
            <Typography variant="body1" fontWeight={"regular"}>
              Field Width
            </Typography>
            <TextField
              type="number"
              name="fieldWidth"
              fullWidth
              placeholder="Field Width"
              value={
                typeof question.properties.fieldWidth === "number"
                  ? question.properties.fieldWidth
                  : ""
              }
              onChange={(e) =>
                onChange("fieldWidth", e.target.value, true, true)
              }
              InputProps={{
                readOnly: isPreview,
              }}
              helperText="Range 5-20"
            />
          </Stack>

          <Stack
            gap={1}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack gap={1} flex={1}>
              <Typography variant="body1" fontWeight={"regular"}>
                Min
              </Typography>
              <TextField
                type="number"
                name="min"
                fullWidth
                placeholder="Min"
                value={
                  typeof question.properties.min === "number"
                    ? question.properties.min
                    : ""
                }
                onChange={(e) => onChange("min", e.target.value, true, true)}
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Stack>

            <Stack gap={1} flex={1}>
              <Typography variant="body1" fontWeight={"regular"}>
                Max
              </Typography>
              <TextField
                type="number"
                name="max"
                fullWidth
                placeholder="Max"
                value={
                  typeof question.properties.max === "number"
                    ? question.properties.max
                    : ""
                }
                onChange={(e) => onChange("max", e.target.value, true, true)}
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Stack>
          </Stack>
        </>
      )}

      {["upload_file"].includes(question.type) && (
        <Stack gap={1}>
          <Typography
            variant="body1"
            fontWeight={"regular"}
            sx={requiredStyles}
          >
            Maximum Number of Files
          </Typography>
          <TextField
            type="number"
            name="max"
            fullWidth
            placeholder="Max"
            value={question.properties.max}
            onChange={(e) => onChange("max", e.target.value, true, true)}
            InputProps={{
              readOnly: isPreview,
              inputProps: { min: 1 },
            }}
          />
        </Stack>
      )}

      {["slider"].includes(question.type) && (
        <>
          <Stack
            gap={1}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack gap={1} flex={1}>
              <Typography
                variant="body1"
                fontWeight={"regular"}
                sx={requiredStyles}
              >
                Min Label
              </Typography>
              <TextField
                name="minLabel"
                fullWidth
                placeholder="Min Label"
                value={question.properties.minLabel}
                onChange={(e) => onChange("minLabel", e.target.value, true)}
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Stack>

            <Stack gap={1} flex={1}>
              <Typography
                variant="body1"
                fontWeight={"regular"}
                sx={requiredStyles}
              >
                Max Label
              </Typography>
              <TextField
                name="maxLabel"
                fullWidth
                placeholder="Max Label"
                value={question.properties.maxLabel}
                onChange={(e) => onChange("maxLabel", e.target.value, true)}
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Stack>
          </Stack>

          <Stack
            gap={1}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack gap={1} flex={1}>
              <Typography
                variant="body1"
                fontWeight={"regular"}
                sx={requiredStyles}
              >
                Min
              </Typography>
              <TextField
                type="number"
                name="min"
                fullWidth
                placeholder="Min"
                value={
                  typeof question.properties.min === "number"
                    ? question.properties.min
                    : ""
                }
                onChange={(e) => onChange("min", e.target.value, true, true)}
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Stack>

            <Stack gap={1} flex={1}>
              <Typography
                variant="body1"
                fontWeight={"regular"}
                sx={requiredStyles}
              >
                Max
              </Typography>
              <TextField
                type="number"
                name="max"
                fullWidth
                placeholder="Max"
                value={
                  typeof question.properties.max === "number"
                    ? question.properties.max
                    : ""
                }
                onChange={(e) => onChange("max", e.target.value, true, true)}
                InputProps={{
                  readOnly: isPreview,
                }}
              />
            </Stack>
          </Stack>

          <Stack gap={1}>
            <Typography
              variant="body1"
              fontWeight={"regular"}
              sx={requiredStyles}
            >
              Step
            </Typography>
            <TextField
              type="number"
              name="step"
              fullWidth
              placeholder="Step"
              value={question.properties.step}
              onChange={(e) => onChange("step", e.target.value, true, true)}
              InputProps={{
                readOnly: isPreview,
              }}
              inputProps={{
                min: 1,
              }}
              helperText={
                question.properties.step <= 0 ? "Must be greater than 0" : null
              }
            />
          </Stack>
        </>
      )}

      {["radio", "checkbox"].includes(question.type) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Orientation
          </Typography>
          <FormControl fullWidth>
            <Select
              readOnly={isPreview}
              fullWidth
              id="orientation"
              value={question.properties.orientation}
              onChange={(e) => onChange("orientation", e.target.value, true)}
            >
              <MenuItem value={"vertical"}>Vertical</MenuItem>
              <MenuItem value={"horizontal"}>Horizontal</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      {![
        "statement",
        "summary",
        "randomize",
        "eligibility",
        "repeated_measure",
      ].includes(question.type) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Helper Text
          </Typography>
          <TextField
            name="helperText"
            fullWidth
            multiline
            rows={2}
            placeholder="Helper Text"
            value={question.helperText || ""}
            onChange={(e) => onChange("helperText", e.target.value)}
            InputProps={{
              readOnly: isPreview,
            }}
          />
        </Stack>
      )}

      {![
        "statement",
        "summary",
        "randomize",
        "eligibility",
        "repeated_measure",
      ].includes(question.type) && (
        <Stack gap={1}>
          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch checked={allowRemark} onChange={handleAllowRemark} />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Allow Remark
              </Typography>
            }
          />
          {allowRemark && (
            <TextField
              name="remarkText"
              fullWidth
              placeholder="Remark"
              value={question.properties.remarkText || ""}
              onChange={(e) => onChange("remarkText", e.target.value, true)}
              InputProps={{
                readOnly: isPreview,
              }}
            />
          )}
        </Stack>
      )}

      {displayMarkdown && (
        <MarkdownPreview
          field="label"
          isProp={false}
          close={closeMarkdown}
          isPreview={isPreview}
        />
      )}
    </Stack>
  );
};
